import { parse } from 'csv-parse/browser/esm/sync';
import { UploadWorkflowContact } from 'services/api/postUploadWorkflowContacts';
import { LeadFieldDefinition } from 'services/api/WorkflowServiceAPI';

const stringHeaders = ['firstName', 'lastName', 'phoneNumber', 'crmStatus', 'crmId', 'planName'] as const;
const jsonHeaders = ['dependents', 'metadata'] as const;
const allHeaders = [...stringHeaders, ...jsonHeaders] as const;

type AllHeader = (typeof allHeaders)[number];

const isBuiltInHeader = (header: string): header is AllHeader => (allHeaders as readonly string[]).includes(header);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const builtInExamples: Record<AllHeader, string> = {
    firstName: 'John',
    lastName: 'Doe',
    phoneNumber: '+15551234567',
    crmStatus: 'active',
    crmId: 'crm-1234',
    planName: 'Gold Plan',
    dependents: '[]',
    metadata: '{}',
};

export interface CsvSchema {
    requiredColumns: string[];
    optionalColumns: string[];
    leadFields: LeadFieldDefinition[];
    parseCSV: (csvText: string) => {
        contacts: UploadWorkflowContact[];
        invalidPhoneCount: number;
        invalidPhonesData: { phoneNumber: string; name: string }[];
        missingHeaders: string[];
        jsonErrors: { row: number; column: string; error: string }[];
    };
}

const reservedHeaderKeys = new Set((allHeaders as readonly string[]).map((header) => header.toLowerCase()));

export const buildCsvSchema = (rawLeadFields: LeadFieldDefinition[] = []): CsvSchema => {
    // The backend rejects lead fields colliding with a built-in column at config-write time;
    // defensively drop them here too so they can never produce a duplicate header.
    const leadFields = rawLeadFields.filter((field) => !reservedHeaderKeys.has(field.key.toLowerCase()));
    const requiredLeadFields = leadFields.filter((field) => field.required);
    const optionalLeadFields = leadFields.filter((field) => !field.required);

    const requiredColumns = ['firstName', 'lastName', 'phoneNumber', ...requiredLeadFields.map((field) => field.key)];
    const optionalColumns = [
        'crmStatus',
        'crmId',
        'planName',
        'dependents',
        'metadata',
        ...optionalLeadFields.map((field) => field.key),
    ];

    const parseCSV = (csvText: string) => {
        const records = parse(csvText, {
            columns: true,
            skip_empty_lines: true,
            trim: true,
            relax_quotes: true,
            relax_column_count: true,
        }) as Record<string, string>[];

        const headers = Object.keys(records[0] || {});
        const requiredHeaders = ['firstName', 'lastName', 'phoneNumber'] as (keyof UploadWorkflowContact)[];
        const missingHeaders: string[] = requiredHeaders.filter((header) => !headers.includes(header));

        for (const field of requiredLeadFields) {
            const matched = headers.some((header) => header.toLowerCase() === field.key.toLowerCase());
            if (!matched) missingHeaders.push(field.key);
        }

        const contacts: UploadWorkflowContact[] = [];
        let invalidPhoneCount = 0;
        const invalidPhonesData: { phoneNumber: string; name: string }[] = [];
        const jsonErrors: { row: number; column: string; error: string }[] = [];

        for (let i = 0; i < records.length; i++) {
            const record = records[i];
            const contact: UploadWorkflowContact = {
                firstName: '',
                lastName: '',
                phoneNumber: '',
            };

            const customValues: Record<string, string> = {};

            for (const header of headers) {
                if ((allHeaders as readonly string[]).includes(header)) {
                    const value = record[header] || '';
                    if (!value) continue;

                    if ((stringHeaders as readonly string[]).includes(header)) {
                        (contact as unknown as Record<string, unknown>)[header] = value;
                    } else if (header === 'dependents') {
                        try {
                            const parsed = JSON.parse(value);
                            if (!Array.isArray(parsed)) {
                                jsonErrors.push({
                                    row: i + 2,
                                    column: 'dependents',
                                    error: 'dependents must be a JSON array',
                                });
                            } else {
                                contact.dependents = parsed;
                            }
                        } catch {
                            jsonErrors.push({
                                row: i + 2,
                                column: 'dependents',
                                error: 'Invalid JSON in dependents column',
                            });
                        }
                    } else if (header === 'metadata') {
                        try {
                            const parsed = JSON.parse(value);
                            if (!isPlainObject(parsed)) {
                                jsonErrors.push({
                                    row: i + 2,
                                    column: 'metadata',
                                    error: 'metadata must be a JSON object',
                                });
                            } else {
                                contact.metadata = parsed;
                            }
                        } catch {
                            jsonErrors.push({
                                row: i + 2,
                                column: 'metadata',
                                error: 'Invalid JSON in metadata column',
                            });
                        }
                    }
                    continue;
                }

                const field = leadFields.find((f) => f.key.toLowerCase() === header.toLowerCase());
                if (!field) continue;
                const value = record[header] || '';
                if (!value) continue;
                customValues[field.key] = value;
            }

            if (Object.keys(customValues).length > 0) {
                contact.metadata = { ...(contact.metadata || {}), ...customValues };
            }

            if (
                contact.phoneNumber &&
                (!contact.phoneNumber.startsWith('+1') || contact.phoneNumber.replace(/\D/g, '').length !== 11)
            ) {
                invalidPhonesData.push({
                    phoneNumber: contact.phoneNumber,
                    name: `${contact.firstName} ${contact.lastName}`,
                });
                invalidPhoneCount++;
            } else {
                contacts.push(contact);
            }
        }

        return { contacts, invalidPhoneCount, invalidPhonesData, missingHeaders, jsonErrors };
    };

    return { requiredColumns, optionalColumns, leadFields, parseCSV };
};

export const escapeCsvCell = (value: string): string => {
    let cell = value;
    if (/^[=+\-@\t\r]/.test(cell)) {
        cell = `'${cell}`;
    }
    if (/[",\r\n]/.test(cell)) {
        cell = `"${cell.replace(/"/g, '""')}"`;
    }
    return cell;
};

export const buildCsvTemplate = (schema: CsvSchema): string => {
    const columns = [...schema.requiredColumns, ...schema.optionalColumns];
    const leadFieldMap = new Map(schema.leadFields.map((field) => [field.key, field]));

    const exampleFor = (column: string): string => {
        const field = leadFieldMap.get(column);
        if (field) return field.example || '';
        return isBuiltInHeader(column) ? builtInExamples[column] : '';
    };

    const headerRow = columns.map((column) => escapeCsvCell(column)).join(',');
    const exampleRow = columns.map((column) => escapeCsvCell(exampleFor(column))).join(',');

    return `${headerRow}\n${exampleRow}`;
};

export const downloadCsvTemplate = (schema: CsvSchema): void => {
    const csv = buildCsvTemplate(schema);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'contacts-template.csv';
    link.click();
    URL.revokeObjectURL(url);
};
